import { useRef, useState } from 'react'
import { ChevronRight } from './ChevronRight'
import { requestRoomNav } from '../lib/navigationBus'
import { leaveRoom } from '../lib/api/rooms'
import { leaveColorfulRoom } from '../games/colorful/api/rooms'
import { leaveThreeStepRoom } from '../games/three-step/api/rooms'
import { leaveWordTvRoomBySession } from '../lib/api/wordTv'
import type { ActiveRoom } from '../lib/api/activeRooms'
import { useT } from '../i18n'

const GAME_META: Record<ActiveRoom['game'], {
  name: string; image: string; turnBorder: string; turnText: string
}> = {
  iet:        { name: "I'D EAT THAT",        image: '/games/ideatthat.webp',
                turnBorder: 'border-2 border-game-iet-dark',        turnText: 'text-game-iet-dark' },
  colorful:   { name: 'COLORFUL',            image: '/games/colorful.webp',
                turnBorder: 'border-2 border-game-colorful-dark',   turnText: 'text-game-colorful-dark' },
  three_step: { name: 'THREE STEP SOLUTION', image: '/games/three-step.webp',
                turnBorder: 'border-2 border-game-three-step-dark', turnText: 'text-game-three-step-dark' },
  fake_out:   { name: 'FAKE OUT',            image: '/games/fake-out.webp',
                turnBorder: 'border-2 border-game-fakeout-dark',    turnText: 'text-game-fakeout-dark' },
  close_enough: { name: 'CLOSE ENOUGH',      image: '/games/close-enough.webp',
                turnBorder: 'border-2 border-game-closeenough',     turnText: 'text-game-closeenough-dark' },
}

const LEAVE_FN: Record<ActiveRoom['game'], (roomId: string) => Promise<void>> = {
  iet:        leaveRoom,
  colorful:   leaveColorfulRoom,
  three_step: leaveThreeStepRoom,
  // Host leaving abandons the room for everyone; a guest just frees
  // their seat. The RPC decides which, from who is calling.
  fake_out:     (roomId: string) => leaveWordTvRoomBySession(roomId),
  close_enough: (roomId: string) => leaveWordTvRoomBySession(roomId),
}

/** "36h" / "45m" until an ISO deadline; "soon" once it has passed. */
function timeLeftLabel(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now()
  if (ms <= 0) return 'soon'
  const h = Math.round(ms / 3_600_000)
  if (h >= 1) return `${h}h`
  return `${Math.max(1, Math.round(ms / 60_000))}m`
}

interface Props {
  room: ActiveRoom
  /** Called after a successful leave so the list can refetch. When
   *  omitted the Leave affordance is hidden and the card is enter-only. */
  onLeft?: () => void
}

/**
 * One row for a running game the player is seated in, used by the
 * Hub's "Jump back in" section and the Your Game Rooms screen.
 *
 * Entering goes through requestRoomNav, the same path a push tap uses,
 * so the card and the push can never route differently.
 *
 * Leaving lives HERE, on the card, rather than on every phase screen:
 * since back now parks a room instead of abandoning it, this is the
 * one place a player can always drop a game they are done with, which
 * also keeps the 10-game cap escapable. Two taps (Leave, then Sure?)
 * so a slip cannot abandon a live game; leaving the last human seat
 * lets the humanless sweep abandon the room within a minute.
 *
 * Not a <button> wrapper: the row holds two separate tap targets, and
 * buttons cannot nest.
 */
export function ActiveRoomCard({ room, onLeft }: Props) {
  const t = useT()
  const meta = GAME_META[room.game]
  const [armed, setArmed] = useState(false)
  const [leaving, setLeaving] = useState(false)
  const disarmTimer = useRef<number | null>(null)

  const line = room.my_turn
    ? (room.turn_deadline_at
        ? t('Your turn · AI plays your seat in {time}', { time: timeLeftLabel(room.turn_deadline_at) })
        : t('Your turn'))
    : room.status === 'lobby' ? t('In the lobby') : t('Waiting on others')

  async function handleLeaveTap() {
    if (!armed) {
      setArmed(true)
      if (disarmTimer.current) window.clearTimeout(disarmTimer.current)
      disarmTimer.current = window.setTimeout(() => setArmed(false), 3000)
      return
    }
    if (disarmTimer.current) window.clearTimeout(disarmTimer.current)
    setLeaving(true)
    try {
      await LEAVE_FN[room.game](room.room_id)
      onLeft?.()
    } catch {
      // Room may already be dead; refetching resolves either way.
      onLeft?.()
    } finally {
      setLeaving(false)
      setArmed(false)
    }
  }

  return (
    <div className={`w-full bg-white rounded-2xl shadow-card p-4 flex items-center gap-3
                     ${room.my_turn ? meta.turnBorder : 'border border-brand-border'}`}>
      <button
        type="button"
        onClick={() => requestRoomNav({ game: room.game, roomId: room.room_id })}
        className="flex-1 min-w-0 flex items-center gap-4 text-left
                   active:scale-[0.98] transition-transform"
      >
        <img src={meta.image} alt={meta.name}
             className="w-10 h-10 rounded-xl object-cover shrink-0" />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-brand-text truncate">{meta.name}</h3>
          <p className="text-xs text-brand-muted truncate">
            {room.players === 1
              ? t('Room {code} · 1 player', { code: room.code })
              : t('Room {code} · {n} players', { code: room.code, n: room.players })} ·{' '}
            <span className={room.my_turn ? `${meta.turnText} font-semibold` : ''}>
              {line}
            </span>
          </p>
        </div>
        <ChevronRight size={20} color="#6B7280" className="shrink-0" />
      </button>

      {onLeft && (
        <button
          type="button"
          onClick={handleLeaveTap}
          disabled={leaving}
          className={`shrink-0 text-xs font-semibold px-3 py-2 rounded-full border
                      active:scale-95 transition-all disabled:opacity-50
                      ${armed
                        ? 'bg-brand-danger text-white border-brand-danger'
                        : 'text-brand-muted border-brand-border'}`}
        >
          {leaving ? '…' : armed ? t('Sure?') : t('Leave')}
        </button>
      )}
    </div>
  )
}
